import React from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Chat from "./Chat/Chat";
import Lobby from "./Lobby/Lobby";
import ParticipantList from "./ParticipantList/ParticipantList";
import ParticipantListScroll from "./ParticipantList/ParticipantListScroll";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { peersLengthSelector } from "../../helpers/Selectors";
import InviteBox from "./ParticipantList/InviteBox";
import { setToolTab } from "../../actions/toolareaActions";
import ChatIcon from "@material-ui/icons/ChatBubbleOutlineOutlined";
import LobbyIcon from "@material-ui/icons/SupervisedUserCircleOutlined";
import Poll from "@material-ui/icons/PollOutlined";
import PeopleIcon from "@material-ui/icons/PeopleOutline";
import Badge from "@material-ui/core/Badge";
import Survey from "./Survey/Survey";
import SurveyCreator from "./Survey/SurveyCreator";

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      zIndex: 999999,
      backgroundColor: theme.palette.background.paper,
    },
    appBar: {
      boxShadow: "0 1px 0 0 #eaedf0",
      backgroundColor: theme.palette.type === "dark" ? "#252C47" : "#fff",
      display: "flex",
      flexDirection: "row",
      padding: theme.spacing(1),
    },
    tabsHeader: {
      flexGrow: 1,
    },
    tabHeader: {
      minWidth: 50,
      fontSize: 12,
    },
    tabText: {
      textTransform: "none",
      lineHeight: 1.17,
      color: "#9fa4b1",
      [theme.breakpoints.down("md")]: {
        display: "none",
      },
    },
    tabActiveText: {
      textTransform: "none",
      lineHeight: 1.17,
      [theme.breakpoints.down("md")]: {
        display: "none",
      },
    },
    tabIcon: {
      [theme.breakpoints.up("lg")]: {
        display: "none",
      },
    },
    closeDrawerButton: {
      border: "1px solid #EAEDF0",
      alignSelf: "center",
      padding: 5,
    },
  })
);

const MeetingDrawer = ({ closeDrawer }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentToolTab = useSelector((state) => state.toolarea.currentToolTab);
  const peersLength = useSelector((state) => peersLengthSelector(state));
  const lobbyPeersLength = useSelector(
    (state) => Object.keys(state.lobbyPeers).length
  );
  const unread = useSelector((state) => state.toolarea.unreadMessages);

  const canCreateSurvey = useSelector((state) =>
    state.me.roles.some((role) =>
      state.room.permissionsFromRoles.SURVEY.includes(role)
    )
  );
  var tabs = [];
  if (lobbyPeersLength > 0) tabs = ["users", "chat", "lobby", "survey"];
  else tabs = ["users", "chat", "survey"];

  return (
    <div className={classes.root}>
      <AppBar position="static" color="default" className={classes.appBar}>
        <Tabs
          className={classes.tabsHeader}
          value={tabs.indexOf(currentToolTab)}
          onChange={(event, value) => dispatch(setToolTab(tabs[value]))}
          variant="fullWidth"
          textColor="secondary"
        >
          <Tab
            className={classes.tabHeader}
            icon={
              <Badge
                className={classes.tabIcon}
                color="secondary"
                badgeContent={peersLength + 1}
              >
                <PeopleIcon />
              </Badge>
            }
            label={
              <div
                className={
                  currentToolTab === "users"
                    ? classes.tabActiveText
                    : classes.tabText
                }
              >
                {intl.formatMessage({
                  id: "label.participants",
                  defaultMessage: "Participants",
                })}
                &nbsp;({peersLength + 1})
              </div>
            }
          />
          <Tab
            className={classes.tabHeader}
            icon={
              <Badge
                className={classes.tabIcon}
                color="secondary"
                badgeContent={unread}
              >
                <ChatIcon />
              </Badge>
            }
            label={
              <div
                className={
                  currentToolTab === "chat"
                    ? classes.tabActiveText
                    : classes.tabText
                }
              >
                {intl.formatMessage({
                  id: "label.chat",
                  defaultMessage: "Chat",
                })}
                &nbsp;({unread})
              </div>
            }
          />
          {lobbyPeersLength > 0 && (
            <Tab
              className={classes.tabHeader}
              icon={
                <Badge
                  className={classes.tabIcon}
                  color="secondary"
                  badgeContent={lobbyPeersLength}
                >
                  <LobbyIcon />
                </Badge>
              }
              label={
                <div
                  className={
                    currentToolTab === "lobby"
                      ? classes.tabActiveText
                      : classes.tabText
                  }
                >
                  {intl.formatMessage({
                    id: "tooltip.lobby",
                    defaultMessage: "Lobby",
                  })}
                  &nbsp;({lobbyPeersLength})
                </div>
              }
            />
          )}
          <Tab
            className={classes.tabHeader}
            icon={
              <Badge
                className={classes.tabIcon}
                color="secondary"
                badgeContent={0}
              >
                <Poll />
              </Badge>
            }
            label={
              <div
                className={
                  currentToolTab === "survey"
                    ? classes.tabActiveText
                    : classes.tabText
                }
              >
                {intl.formatMessage({
                  id: "tooltip.survey",
                  defaultMessage: "Survey",
                })}
              </div>
            }
          />
        </Tabs>
        <IconButton
          onClick={closeDrawer}
          className={classes.closeDrawerButton}
          aria-label="Close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      {(currentToolTab === "chat" || currentToolTab === "privateChat") && (
        <Chat />
      )}
      {currentToolTab === "users" && (
        <React.Fragment>
          <ParticipantListScroll>
            <ParticipantList />
          </ParticipantListScroll>
          <InviteBox />
        </React.Fragment>
      )}
      {currentToolTab === "lobby" && <Lobby />}
      {currentToolTab === "survey" && (
        <React.Fragment>
          <ParticipantListScroll>
            <Survey />
          </ParticipantListScroll>
          {canCreateSurvey && <SurveyCreator />}
        </React.Fragment>
      )}
    </div>
  );
};

MeetingDrawer.propTypes = {
  closeDrawer: PropTypes.func.isRequired,
};

export default MeetingDrawer;
